/**
 * Excel 式列头筛选下拉组件
 * 点击表头 → 弹出该列所有唯一值 → 勾选/取消勾选 → 过滤数据
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

interface ColumnFilterDropdownProps {
  columnKey: string;
  label: string;
  /** 该列在所有数据中的唯一值列表 */
  uniqueValues: string[];
  /** 当前选中的值集合（空 = 不筛选，全部显示） */
  selectedValues: Set<string>;
  onFilterChange: (columnKey: string, selected: Set<string>) => void;
  align?: 'left' | 'right';
}

export function ColumnFilterDropdown({
  columnKey,
  label,
  uniqueValues,
  selectedValues,
  onFilterChange,
  align = 'left',
}: ColumnFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const isFiltered = selectedValues.size > 0 && selectedValues.size < uniqueValues.length;

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return uniqueValues;
    const q = search.toLowerCase();
    return uniqueValues.filter((v) => v.toLowerCase().includes(q));
  }, [uniqueValues, search]);

  const toggleValue = (val: string) => {
    const next = new Set(selectedValues);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    // 如果全选了，等价于不筛选 → 清空
    if (next.size === uniqueValues.length) next.clear();
    onFilterChange(columnKey, next);
  };

  const selectAll = () => { onFilterChange(columnKey, new Set()); };
  const selectNone = () => { onFilterChange(columnKey, new Set(['__NONE__'])); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className={`group flex w-full items-center gap-1 px-4 py-3 text-xs font-medium uppercase tracking-[0.08em] transition ${
          align === 'right' ? 'justify-end' : 'justify-start'
        }`}
        style={{ color: isFiltered ? 'var(--color-key)' : 'var(--color-system-secondary)' }}
      >
        {label}
        <ChevronDown
          size={10}
          className="shrink-0 opacity-40 transition group-hover:opacity-100"
          style={isFiltered ? { color: 'var(--color-key)', opacity: 1 } : undefined}
        />
      </button>

      {open && (
        <div
          className="absolute top-full z-30 mt-0.5 w-52 rounded-xl border shadow-lg"
          style={{
            backgroundColor: 'var(--color-background-primary)',
            borderColor: 'var(--color-system-quaternary)',
            left: align === 'right' ? 'auto' : 0,
            right: align === 'right' ? 0 : 'auto',
          }}
        >
          {/* 搜索框 */}
          {uniqueValues.length > 6 && (
            <div className="border-b px-2 py-1.5" style={{ borderColor: 'var(--color-system-quaternary)' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索..."
                className="w-full rounded border-none bg-transparent px-1.5 py-1 text-xs outline-none"
                autoFocus
              />
            </div>
          )}

          {/* 全选 / 清除 */}
          <div
            className="flex items-center justify-between border-b px-3 py-1.5"
            style={{ borderColor: 'var(--color-system-quaternary)' }}
          >
            <button
              type="button"
              onClick={selectAll}
              className="text-[10px] font-medium transition hover:opacity-70"
              style={{ color: 'var(--color-key)' }}
            >
              全选
            </button>
            <button
              type="button"
              onClick={selectNone}
              className="text-[10px] font-medium transition hover:opacity-70"
              style={{ color: 'var(--color-system-tertiary)' }}
            >
              清除
            </button>
          </div>

          {/* 值列表 */}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-center text-[11px]" style={{ color: 'var(--color-system-tertiary)' }}>
                无匹配项
              </div>
            ) : (
              filtered.map((val) => {
                const checked = selectedValues.size === 0 || selectedValues.has(val);
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => {
                      if (selectedValues.size === 0) {
                        // 从"全选"状态点击某项 → 变成"只选这一项以外的都选"
                        const all = new Set(uniqueValues);
                        all.delete(val);
                        onFilterChange(columnKey, all);
                      } else {
                        toggleValue(val);
                      }
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-[var(--color-background-secondary)]"
                  >
                    <span
                      className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border"
                      style={{
                        borderColor: checked ? 'var(--color-key)' : 'var(--color-system-quaternary)',
                        backgroundColor: checked ? 'var(--color-key)' : 'transparent',
                      }}
                    >
                      {checked && <Check size={9} color="#fff" strokeWidth={3} />}
                    </span>
                    <span
                      className="truncate"
                      style={{ color: checked ? 'var(--color-system-primary)' : 'var(--color-system-tertiary)' }}
                    >
                      {val || '（空）'}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* 已筛选提示 */}
          {isFiltered && (
            <div
              className="flex items-center justify-between border-t px-3 py-1.5"
              style={{ borderColor: 'var(--color-system-quaternary)' }}
            >
              <span className="text-[10px]" style={{ color: 'var(--color-system-tertiary)' }}>
                已选 {selectedValues.size}/{uniqueValues.length}
              </span>
              <button
                type="button"
                onClick={() => { onFilterChange(columnKey, new Set()); setOpen(false); }}
                className="flex items-center gap-0.5 text-[10px] font-medium transition hover:opacity-70"
                style={{ color: 'var(--color-key)' }}
              >
                <X size={9} />清除筛选
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 活跃筛选标签条
// ---------------------------------------------------------------------------
interface ActiveFilterBadgesProps {
  columnFilters: Map<string, Set<string>>;
  columns: { key: string; label: string }[];
  onClear: (key: string) => void;
  onClearAll: () => void;
}

export function ActiveFilterBadges({ columnFilters, columns, onClear, onClearAll }: ActiveFilterBadgesProps) {
  const activeFilters = Array.from(columnFilters.entries()).filter(([, v]) => v.size > 0);
  if (activeFilters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-5 py-2 text-xs" style={{ color: 'var(--color-system-secondary)' }}>
      <span style={{ color: 'var(--color-system-tertiary)' }}>筛选:</span>
      {activeFilters.map(([key, values]) => {
        const col = columns.find((c) => c.key === key);
        const label = col?.label || key;
        const preview = Array.from(values).slice(0, 2).join(', ');
        const more = values.size > 2 ? ` +${values.size - 2}` : '';
        return (
          <span
            key={key}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
            style={{ backgroundColor: 'rgba(0,113,227,0.08)', color: 'var(--color-key)' }}
          >
            <span className="font-medium">{label}:</span> {preview}{more}
            <button type="button" onClick={() => onClear(key)} className="ml-0.5 transition hover:opacity-60">
              <X size={10} />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={onClearAll}
        className="text-[10px] font-medium underline transition hover:opacity-60"
        style={{ color: 'var(--color-system-tertiary)' }}
      >
        清除全部
      </button>
    </div>
  );
}
